import type { MonospaceWorkspace } from '#layers/data-model/public';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useAuth } from '#layers/access/stores/auth';
import { useSystemEntityStore } from '#layers/runtime/public';

/**
 * The workspaces the current user can access, resolved through their memberships. A non-admin
 * member cannot list all workspaces (that endpoint is admin-only), so read the current user by
 * key and pull workspaces through the memberships relation. `cache-and-fetch` so returning to a
 * view refetches in the background instead of serving a stale membership list.
 */
export function useMemberWorkspaces() {
	const store = useSystemEntityStore();
	const { currentUser } = storeToRefs(useAuth());

	const currentUserId = computed(() => currentUser.value?.id ?? null);

	const userQuery = store.MonospaceUser.query((q) => currentUserId.value
		? q.first({
				key: currentUserId.value,
				include: { workspaces: { include: { workspace: {} } } },
				fetchPolicy: 'cache-and-fetch',
			})
		: q.first({ enabled: false }));

	const memberWorkspaces = computed<MonospaceWorkspace[]>(() =>
		(userQuery.data.value?.workspaces ?? [])
			.flatMap((member) => (member.workspace ? [member.workspace] : [])));

	return { memberWorkspaces, isLoading: userQuery.loading };
}
